import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { Form, Row, Col, Button, Select, Input, DatePicker, Space, Table, message } from 'antd';
import dayjs from 'dayjs';
import { useReactToPrint } from 'react-to-print';
import type { ColumnsType, SorterResult } from 'antd/es/table/interface';

import system from '../../../config/systematic';
import getList from '../../universal/getList';
import DownloadExcel from '../../report/DownloadExcel';
import fetchApi from '../../../utils/api/fetchApi';

const { RangePicker } = DatePicker;
const wmService: any = system.wmService;

// ================= 类型 =================
interface SourceItem {
  productId: string;
  productName: string;
  productUnitId?: string;
  productUnitName: string;
  quotationOrderId: string;
  submissionTime: string;
  univalence: number;
  buyerId?: string;
  buyerName?: string;
  clientUserId?: string;
}

interface RowData {
  key: string;
  productId: string;
  productName: string;
  productUnitName: string;
  oldPrice?: number;
  [quotationId: string]: any; // ✅ 动态报价列直接挂身上
}

// ================= 组件 =================
export default function BuyerComparison() {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [rawData, setRawData] = useState<SourceItem[]>([]);
  const [buyerData, setBuyerData] = useState<any>();
  const [clientUserData, setClientUserData] = useState<any>();
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<'ascend' | 'descend' | null>(null);
  const contentRef = React.useRef<HTMLDivElement>(null);

  const [oldPriceMap, setOldPriceMap] = useState<Record<string, number>>({});
  const reqRef = React.useRef(0);

  // ================= ① 报价单列（去重排序） =================
  const quotationColumns = useMemo(() => {
    const map = new Map<string, { id: string; date: string; timestamp: number }>();
    rawData.forEach((item) => {
      const qid = item.quotationOrderId;
      if (!qid || map.has(qid)) return;
      const t = dayjs(item.submissionTime);
      map.set(qid, {
        id: qid,
        date: t.isValid() ? `${t.year()}/${t.month() + 1}/${t.date()}` : '',
        timestamp: t.isValid() ? t.valueOf() : 0,
      });
    });
    return Array.from(map.values()).sort((a, b) => a.timestamp - b.timestamp);
  }, [rawData]);

  // ================= ② 产品行 + 价格直接挂 row 上 =================
  const productRows = useMemo(() => {
    const map = new Map<string, RowData>();

    rawData.forEach((item) => {
      const pid = String(item.productId || '').trim();
      if (!pid) return;

      if (!map.has(pid)) {
        map.set(pid, {
          key: pid,
          productId: pid,
          productName: item.productName,
          productUnitName: item.productUnitName,
        });
      }

      const row = map.get(pid)!;
      if (item.quotationOrderId) {
        row[item.quotationOrderId] = item.univalence;
      }
    });

    return Array.from(map.values());
  }, [rawData]);

  // ================= ③ 过滤：全0行不要 =================
  const filteredRows = useMemo(() => {
    return productRows.filter((row) => {
      return quotationColumns.some((qc) => {
        const p = row[qc.id];
        return p != null && p > 0;
      });
    });
  }, [productRows, quotationColumns]);

  // ================= ④ 并发拉 oldPrice =================
  useEffect(() => {
    if (!filteredRows.length) {
      setOldPriceMap({});
      return;
    }

    const version = ++reqRef.current;
    const timeRange = form.getFieldValue('submissionTime');
    const submissionTime = Array.isArray(timeRange) && timeRange[0] && timeRange[1]
      ? [timeRange[0].format('YYYY-MM-DD'), timeRange[1].format('YYYY-MM-DD')]
      : undefined;

    setOldPriceMap((prev) => {
      const next: Record<string, number> = {};
      filteredRows.forEach((r) => { next[r.key] = r.key in prev ? prev[r.key] : -1; });
      return next;
    });

    filteredRows.forEach((row) => {
      fetchApi
        .GET(wmService, {
          apiId: 'quotationOrder',
          apiExtend: 'quotationProductComparisonExEx',
          apiData: {
            productUnitName: row.productUnitName,
            productName: row.productName,
            submissionTime,
            mountDataOptions: { userMount: { isDept: true } },
          },
        })
        .then((res: any) => {
          if (version !== reqRef.current) return;
          setOldPriceMap((prev) => ({
            ...prev,
            [row.key]: res?.success ? res.data?.oldPrice ?? 0 : 0,
          }));
        })
        .catch(() => {
          if (version !== reqRef.current) return;
          setOldPriceMap((prev) => ({ ...prev, [row.key]: 0 }));
        });
    });
  }, [filteredRows, form]);

  // ================= ⑤ 排序 + 合并 oldPrice =================
  const displayRows = useMemo(() => {
    let rows = filteredRows;

    if (sortField && sortOrder) {
      const dir = sortOrder === 'ascend' ? 1 : -1;
      rows = [...rows].sort((a, b) => {
        const av = a[sortField] ?? 0;
        const bv = b[sortField] ?? 0;
        return (av - bv) * dir;
      });
    }

    return rows.map((r) => ({
      ...r,
      oldPrice: oldPriceMap[r.key] === -1 ? undefined : oldPriceMap[r.key],
    }));
  }, [filteredRows, sortField, sortOrder, oldPriceMap]);

  // ================= ⑥ 列定义 =================
  const columns: Array<any> = useMemo(() => {
    const fixed = [
      { title: '产品', dataIndex: 'productName', header: '产品', key: 'productName', width: 20, fixed: 'left' },
      { title: '单位', dataIndex: 'productUnitName', header: '单位', key: 'productUnitName', width: 10, fixed: 'left' },
      {
        title: '进货价',
        header: '进货价',
        key: 'oldPrice',
        dataIndex: 'oldPrice',
        width: 15,
        align: 'center',
        render: (_: any, record: RowData) =>
          record.oldPrice === undefined
            ? <span style={{ color: '#bbb' }}>…</span>
            : record.oldPrice.toFixed(2),
      },
    ];

    const dynamic = quotationColumns.map((qc) => ({
      title: <div style={{ textAlign: 'center', lineHeight: 1.4 }}><div>{qc.date}</div></div>,
      key: qc.id,
      dataIndex: qc.id,
      header: qc.date,
      width: 20,
      align: 'center' as const,
      sortOrder: sortField === qc.id ? sortOrder : null,
      sorter: true,
      render: (_: any, record: RowData) => {
        const p = record[qc.id];
        return p != null ? p.toFixed(2) : '-';
      },
    }));

    return [...fixed, ...dynamic];
  }, [quotationColumns, sortField, sortOrder]);

  // ================= ⑦ 排序回调 =================
  const handleTableChange = useCallback((_p: any, _f: any, sorter: SorterResult<any> | SorterResult<any>[]) => {
    if (Array.isArray(sorter)) return;
    setSortField((sorter.column?.key as string) || null);
    setSortOrder(sorter.order || null);
  }, []);

  // ================= ⑧ 初始化日期 =================
  useEffect(() => {
    form.setFieldValue('submissionTime', [
      dayjs().subtract(1, 'month').startOf('day'),
      dayjs().endOf('day'),
    ]);
  }, [form]);

  // ================= ⑨ 查询 =================
  const onFinish = async () => {
    reqRef.current++;
    setOldPriceMap({});
    setLoading(true);
    try {
      const values = await form.validateFields();
      const params = {
        ...values,
        submissionTime: values.submissionTime
          ? [values.submissionTime[0].format('YYYY-MM-DD'), values.submissionTime[1].format('YYYY-MM-DD')]
          : undefined,
        mountDataOptions: { userMount: { isDept: true } },
      };
      const res = await fetchApi.GET(wmService, {
        apiId: 'quotationOrder',
        apiExtend: 'buyerComparison',
        apiData: params,
      });
      if (!res.success) {
        message.error(res.message || '查询失败');
        setLoading(false);
        return;
      }
      setRawData((res.data || []) as SourceItem[]);
      setSortField(null);
      setSortOrder(null);
    } catch (e: any) {
      message.error(e?.message || '请求失败');
    } finally {
      setLoading(false);
    }
  };

  // ================= ⑩ 打印 / 标题 =================
  const handlePrint = useReactToPrint({ contentRef, documentTitle: '报价对比表' });

  const dateTitle = useMemo(() => {
    const t = form.getFieldValue('submissionTime');
    if (Array.isArray(t) && t[0] && t[1]) {
      return `日期：${t[0].format('YYYY-MM-DD')} 至 ${t[1].format('YYYY-MM-DD')}`;
    }
    return '报价对比表';
  }, [form, rawData]);

  // ================= 渲染 =================
  return (
    <div>
      <div className="no-print">
        <Form form={form} onFinish={onFinish}>
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item name="submissionTime" label="日期范围">
                <RangePicker format="YYYY-MM-DD" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item name="buyerId" label="采购商">
                <Select showSearch allowClear placeholder="采购商" optionFilterProp="children"
                  onSearch={(e) => getList.search('buyer', setBuyerData, e)}>
                  {getList.generalEx(buyerData, { itemName: 'buyerName', includeNull: true })}
                </Select>
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item name="clientUserId" label="客户">
                <Select showSearch allowClear placeholder="客户" optionFilterProp="children"
                  onSearch={(e) => getList.search('clientUser', setClientUserData, e)}>
                  {getList.generalEx(clientUserData, { itemName: 'nickName', includeNull: true })}
                </Select>
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item name="queryString" label="查询">
                <Input placeholder="产品名称" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item>
                <Space>
                  <Button type="primary" loading={loading} htmlType="submit">统计</Button>
                  <Button onClick={handlePrint}>打印</Button>
                  <Button onClick={() => DownloadExcel(displayRows, 'buyerComparison', {
                    date: form.getFieldValue('submissionTime'),
                    columnsHead: columns,
                  })}>
                    导出Excel
                  </Button>
                </Space>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </div>

      <div className="print-table" ref={contentRef} style={{ marginTop: 16 }}>
        <Table
          rowKey="key"
          columns={columns}
          dataSource={displayRows}
          pagination={false}
          scroll={{ x: 'max-content' }}
          bordered size="small"
          loading={loading}
          onChange={handleTableChange}
          title={() => (
            <div style={{ textAlign: 'center', fontWeight: 'bold', fontSize: 15 }}>{dateTitle}</div>
          )}
        />
      </div>

      <style dangerouslySetInnerHTML={{ __html: `
        @media print {
          @page { margin: 0; }
          .no-print { display: none !important; }
          .print-table { display: block !important; }
          .print-table table { border-collapse: collapse !important; width: 100% !important; table-layout: auto !important; }
          .print-table .ant-table-thead > tr > th {
            background: #fff !important; color: #000 !important; border: 1px solid #000 !important;
            font-weight: bold !important; font-size: 12px !important; padding: 6px 8px !important;
          }
          .print-table .ant-table-tbody > tr > td {
            background: #fff !important; color: #000 !important; border: 1px solid #000 !important;
            font-size: 11px !important; padding: 4px 8px !important; white-space: nowrap !important;
          }
          .print-table tr { page-break-inside: avoid !important; }
          body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          .print-table .ant-table-cell-fix-left, .print-table .ant-table-cell-fix-right { position: static !important; }
          .print-table .ant-table-container, .print-table .ant-table-content, .print-table .ant-table-body {
            overflow: visible !important; max-height: none !important;
          }
        }
      ` }} />
    </div>
  );
}